/**
 * Items, the goods a business keeps (migration 0086; the inventory plan's
 * §14.7, step I1b). A list with what is on hand and its value, and an inline
 * form for a new item or a change to one, in the Matching screen's pattern.
 *
 * WHAT AN ITEM'S THREE ACCOUNTS ARE FOR. A sale's cost entry (§14.2) takes
 * the goods out of the item's stock account and puts their cost on its cost
 * account; Add Item on an invoice credits its revenue account. An item with
 * no stock or cost account can be received and given away, and is refused a
 * sale in a sentence that says to set both here.
 *
 * An item with lines is archived, never deleted (0086), and an archived item
 * takes no new line. Archive and Restore are therefore the only removal.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessNotice } from '../components/AccessNotice.js';
import { InfoNote } from '../components/InfoNote.js';
import { Toast } from '../components/Toast.js';
import { useCan } from '../auth/AuthProvider.js';
import { ApiError, api, type Account, type Item, type ItemBody } from '../api.js';
import { formatCents } from '../lib/money.js';

interface Draft {
  name: string;
  sku: string;
  unit: string;
  stock: string;
  cost: string;
  revenue: string;
}

const BLANK: Draft = { name: '', sku: '', unit: 'each', stock: '', cost: '', revenue: '' };

/** "12.5000" as "12.5", "3.0000" as "3". */
export function showQuantity(quantity: string): string {
  return quantity.includes('.') ? quantity.replace(/\.?0+$/, '') : quantity;
}

export function ItemsPage() {
  const mayEdit = useCan()('edit_entries');
  const [items, setItems] = useState<Item[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The form: a new item ('new'), an item's id, or closed. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.listItems(includeArchived), api.listAccounts()])
      .then(([itemResult, accountResult]) => {
        setItems(itemResult.items);
        setAccounts(accountResult.accounts);
        setError(null);
      })
      .catch((caught: unknown) => setError(caught instanceof ApiError ? caught.message : 'Could not read the items.'))
      .finally(() => setLoading(false));
  }, [includeArchived]);
  useEffect(load, [load]);

  const stockAccounts = useMemo(() => accounts.filter((account) => account.inventoryRole === 'stock'), [accounts]);
  const costAccounts = useMemo(() => accounts.filter((account) => account.type === 'expense' && account.isActive), [accounts]);
  const revenueAccounts = useMemo(() => accounts.filter((account) => account.type === 'revenue' && account.isActive), [accounts]);
  const accountName = (id: string | null) => {
    const account = accounts.find((candidate) => candidate.id === id);
    return account ? `${account.code} ${account.name}` : '';
  };

  function open(item: Item | null) {
    setError(null);
    if (item === null) {
      // A new item starts on the business's first stock and cost-of-sales accounts, which it may change.
      setDraft({
        ...BLANK,
        stock: stockAccounts[0]?.id ?? '',
        cost: accounts.find((account) => account.inventoryRole === 'cost_of_sales')?.id ?? '',
      });
      setEditing('new');
      return;
    }
    setDraft({
      name: item.name,
      sku: item.sku ?? '',
      unit: item.unit,
      stock: item.defaultStockAccountId ?? '',
      cost: item.defaultCostAccountId ?? '',
      revenue: item.defaultRevenueAccountId ?? '',
    });
    setEditing(item.id);
  }

  async function save() {
    // Refused out loud rather than by a disabled button (the conventions' "a control must never do nothing").
    if (draft.name.trim() === '' || draft.unit.trim() === '') {
      setError('An item has a name and a unit, such as each, lb or case.');
      return;
    }
    const body: ItemBody = {
      name: draft.name.trim(),
      sku: draft.sku.trim() === '' ? null : draft.sku.trim(),
      unit: draft.unit.trim(),
      defaultStockAccountId: draft.stock === '' ? null : draft.stock,
      defaultCostAccountId: draft.cost === '' ? null : draft.cost,
      defaultRevenueAccountId: draft.revenue === '' ? null : draft.revenue,
    };
    setBusy(true);
    try {
      if (editing === 'new') {
        const { item } = await api.createItem(body);
        setNotice(`Added ${item.name}`);
      } else if (editing !== null) {
        const { item } = await api.updateItem(editing, body);
        setNotice(`Saved ${item.name}`);
      }
      setEditing(null);
      setError(null);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save that item.');
    } finally {
      setBusy(false);
    }
  }

  async function archive(item: Item, archived: boolean) {
    setBusy(true);
    try {
      await api.updateItem(item.id, { archived });
      setNotice(archived ? `${item.name} archived; it takes no new line` : `${item.name} restored`);
      setError(null);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that item.');
    } finally {
      setBusy(false);
    }
  }

  const select = (label: string, value: string, options: Account[], set: (value: string) => void) => (
    <select aria-label={label} value={value} onChange={(event) => set(event.target.value)}>
      <option value="">None</option>
      {options.map((account) => (
        <option key={account.id} value={account.id}>
          {account.code} {account.name}
        </option>
      ))}
    </select>
  );

  /*
   * The form takes the whole row, its fields wrapping as the width allows,
   * rather than one field per column: squeezed into the columns, three
   * selects and two buttons pushed the table past the screen (the preview of
   * 2026-10-15).
   */
  const form = (
    <tr className="item-form">
      <td colSpan={8}>
        <div className="item-form-fields">
          <label>
            Name{' '}
            <input aria-label="Item Name" value={draft.name} onChange={(event) => { const name = event.target.value; setDraft((current) => ({ ...current, name })); }} />
          </label>
          <label>
            SKU{' '}
            <input aria-label="SKU" className="item-short" value={draft.sku} onChange={(event) => { const sku = event.target.value; setDraft((current) => ({ ...current, sku })); }} />
          </label>
          <label>
            Unit{' '}
            <input aria-label="Unit" className="item-short" value={draft.unit} onChange={(event) => { const unit = event.target.value; setDraft((current) => ({ ...current, unit })); }} />
          </label>
          <label>Stock Account {select('Stock Account', draft.stock, stockAccounts, (stock) => setDraft((current) => ({ ...current, stock })))}</label>
          <label>Cost Account {select('Cost Account', draft.cost, costAccounts, (cost) => setDraft((current) => ({ ...current, cost })))}</label>
          <label>Revenue Account {select('Revenue Account', draft.revenue, revenueAccounts, (revenue) => setDraft((current) => ({ ...current, revenue })))}</label>
          <span className="item-form-actions">
            <button type="button" onClick={() => void save()} disabled={busy}>
              {editing === 'new' ? 'Add Item' : 'Save Item'}
            </button>{' '}
            <button type="button" className="link" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </span>
        </div>
      </td>
    </tr>
  );

  return (
    <section className="items">
      <header className="row triage-head">
        <h1>Items</h1>
        <span className="muted">{loading ? 'Loading…' : `${items.length} shown`}</span>
      </header>
      <AccessNotice capability="edit_entries" what="items" />
      <p className="muted">
        The goods the business keeps, with what is on hand today.{' '}
        <InfoNote label="What An Item Is">
          Goods are recorded on a stock account, with the item and how many of its unit. Goods leave first in, first out,
          and are priced when the entry posts, by what they took from stock. A sale records the cost of what it sold on an
          entry of its own, from the item&apos;s stock account to its cost account; set both before selling it. An item
          with entries is archived, never deleted, and an archived item takes no new line.
        </InfoNote>
      </p>
      <div className="row">
        <label>
          <input type="checkbox" checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)} /> Include Archived
        </label>
        {mayEdit && editing === null && (
          <button type="button" onClick={() => open(null)}>
            New Item
          </button>
        )}
        {/* 0088 (§15.2): the count sheet, for anyone; posting a count is edit_entries. */}
        {stockAccounts.length > 0 && (
          <Link to="/items/count">
            Count Stock
          </Link>
        )}
      </div>
      {stockAccounts.length === 0 && !loading && (
        <p className="notice notice-warn">
          No account holds stock yet. Mark one with Hold Stock on Accounts, and goods can be recorded on it.
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {notice && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      <div className="table-scroll">
        <table className="lines items-table" aria-label="Items">
          <thead>
            <tr>
              <th>Name</th>
              <th>SKU</th>
              <th>Unit</th>
              <th>Stock Account</th>
              <th>Cost Account</th>
              <th>Revenue Account</th>
              <th className="num">On Hand</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {editing === 'new' && form}
            {items.map((item) =>
              editing === item.id ? (
                <Fragment key={item.id}>{form}</Fragment>
              ) : (
                <tr key={item.id} className={item.archivedAt === null ? undefined : 'is-inactive'}>
                  <td>
                    {item.name}
                    {item.archivedAt !== null && <span className="tag">Archived</span>}{' '}
                    <Link to={`/items/${item.id}`} aria-label={`History of ${item.name}`}>
                      History
                    </Link>
                    {mayEdit && (
                      <span className="row-actions">
                        {' '}
                        <button type="button" className="link" aria-label={`Edit ${item.name}`} onClick={() => open(item)}>
                          Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="link"
                          aria-label={`${item.archivedAt === null ? 'Archive' : 'Restore'} ${item.name}`}
                          onClick={() => void archive(item, item.archivedAt === null)}
                        >
                          {item.archivedAt === null ? 'Archive' : 'Restore'}
                        </button>
                      </span>
                    )}
                  </td>
                  <td>{item.sku ?? ''}</td>
                  <td>{item.unit}</td>
                  <td>{accountName(item.defaultStockAccountId)}</td>
                  <td>{accountName(item.defaultCostAccountId)}</td>
                  <td>{accountName(item.defaultRevenueAccountId)}</td>
                  <td className="num">{showQuantity(item.onHand)}</td>
                  <td className="num">{formatCents(item.valueCents)}</td>
                </tr>
              ),
            )}
            {items.length === 0 && editing === null && !loading && (
              <tr>
                <td colSpan={8} className="muted">
                  No items yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
